import type { NetworkConfig, Source } from '@/api/types'

/**
 * Whether a source is fed by a passive monitoring agent rather than scanned
 * from the server.
 *
 * Live monitoring is not a separate modality or source type: a network source
 * carries a `mode` setting, and that setting is what decides whether the
 * server connects to targets or waits for an agent's uploads. An absent mode
 * means probe — every network source created before live monitoring existed
 * is in that position.
 */
export function isLiveNetworkSource(source: Pick<Source, 'source_type' | 'config'> | null | undefined): boolean {
  if (!source || source.source_type !== 'network') return false
  return networkConfigOf(source)?.mode === 'live'
}

/** The source's network config, or null when it is not a network source. */
export function networkConfigOf(
  source: Pick<Source, 'source_type' | 'config'> | null | undefined,
): NetworkConfig | null {
  if (!source || source.source_type !== 'network') return null
  return (source.config ?? null) as unknown as NetworkConfig | null
}

/**
 * "node-1 / any" — what a live monitor covers, for display beside its name.
 * Returns null until the agent has reported it on its first upload.
 */
export function liveMonitorScope(config: NetworkConfig | null): string | null {
  if (!config) return null
  const parts = [config.node, config.interface].filter(Boolean)
  return parts.length > 0 ? parts.join(' / ') : null
}
